<template>
    <div class="muven-card">
        <div class="muven-card-title">
            <p>
                {{title}}

                <span class="btn-group btn-group-sm float-right sales-breakdown-mode" role="group">
                    <button type="button"
                        class="btn"
                        v-for="m in MODES"
                        :key="m.key"
                        :class="mode === m.key ? 'btn-secondary' : 'btn-outline-secondary'"
                        :disabled="loading"
                        @click="changeMode(m.key)">
                        {{m.label}}
                    </button>
                </span>
            </p>

            <p class="muven-card-subtitle">
                <span>{{filter.dateSubmitted.start | dateFilter}}</span> até <span>{{filter.dateSubmitted.end | dateFilter}}</span>
                <span v-show="filter.hasChannelList()">
                    <br>
                    <span>Canais: {{filter.getChannelListDescription()}}</span>
                </span>
                <span v-show="filter.hasStoreList()">
                    <br>
                    <span>Lojas: {{filter.getStoreListDescription()}}</span>
                </span>
            </p>
        </div>

        <div class="sales-breakdown-body">
            <div class="sales-breakdown-chart">
                <highcharts :options="chartOptions" />
            </div>

            <div class="sales-breakdown-ranking">
                <p class="sales-breakdown-ranking-total" v-if="!loading">
                    <span>Total</span>
                    <strong>{{formatValue(breakdown.total)}}</strong>
                </p>

                <ul v-if="breakdown.ranking.length">
                    <li v-for="item in breakdown.ranking" :key="item.id">
                        <div class="sales-breakdown-ranking-row">
                            <span class="sales-breakdown-ranking-name">
                                <ChannelLogoCmp v-if="channelById(item.id)" :channel="channelById(item.id)" :size="18" />
                                <template v-else>
                                    <span class="sales-breakdown-dot" :style="{backgroundColor: item.color}"></span>
                                    {{item.name}}
                                </template>
                            </span>
                            <span class="sales-breakdown-ranking-value">
                                {{formatValue(item.total)}}
                                <small>{{formatShare(item.share)}}</small>
                            </span>
                        </div>
                        <div class="sales-breakdown-bar">
                            <span :style="{width: `${item.share}%`, backgroundColor: item.color}"></span>
                        </div>
                    </li>
                </ul>

                <p class="sales-breakdown-empty is-error" v-else-if="!loading && error">Não foi possível carregar as vendas agora.</p>
                <p class="sales-breakdown-empty" v-else-if="!loading">Sem vendas no período.</p>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import { format } from 'date-fns'
import dashboardService from '@/dashboard/services/DashboardService'
import { DashboardBaseFilterModel } from '@/dashboard/models/DashboardBaseFilterModel'
import { buildBreakdown, cloneFilterWith } from '@/dashboard/normalizers/DashboardKpiNormalizer'
import { getChannelBrand } from '@/channel/models/ChannelBrand'
import ChannelLogoCmp from '@/channel/components/ChannelLogoCmp.vue'
import { createGraphic } from '@/utils/normalizers/GraphicNormalizer'
import { adjustsDate } from '@/utils/normalizers/DateNormalizer'
import { dateFilter } from '@/utils/filters/DateTimeFilter'

const MODES = [
    {key: 'money', label: 'Valor'},
    {key: 'sales', label: 'Pedidos'},
]

const DIMENSION_CHANNEL = 'channel'
const DIMENSION_STORE = 'store'

export default {
    name: 'SalesBreakdownCmp',
    components: {
        ChannelLogoCmp,
    },
    props: {
        dimension: {
            type: String,
            default: DIMENSION_CHANNEL,
            validator: (v) => [DIMENSION_CHANNEL, DIMENSION_STORE].includes(v),
        },
        channelList: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            MODES,
            mode: 'money',
            loading: true,
            error: false,
            filter: new DashboardBaseFilterModel(),
            // Resposta crua por modo; zera quando o filtro muda.
            cache: {},
            groups: [],
        }
    },
    filters: {
        dateFilter,
    },
    computed: {
        ...mapState({
            storeListState: (state) => state.storeList,
        }),
        isChannel() {
            return this.dimension === DIMENSION_CHANNEL
        },
        title() {
            return this.isChannel ? 'Vendas por canal' : 'Vendas por loja'
        },
        isMoney() {
            return this.mode === 'money'
        },
        // Nome e cor saem daqui, e não da resposta: quando a lista de canais chega
        // depois do gráfico, o card se corrige sem refazer a chamada.
        breakdown() {
            return buildBreakdown(this.groups.map((group) => this._entryFor(group)))
        },
        chartOptions() {
            const vm = this

            return createGraphic({
                chart: {
                    type: 'column',
                    height: 300,
                },
                xAxis: {
                    categories: this.breakdown.categories.map((c) => format(adjustsDate(c), 'dd/MM')),
                },
                legend: {
                    enabled: false,
                },
                tooltip: {
                    shared: true,
                    formatter() {
                        const rows = this.points
                            .filter((p) => p.y > 0)
                            .map((p) => `<span style="color:${p.color}">●</span> ${p.series.name}: <b>${vm.formatValue(p.y)}</b>`)

                        return [`<b>${this.x}</b>`, ...rows, `Total: <b>${vm.formatValue(this.points[0].total)}</b>`].join('<br>')
                    },
                },
                plotOptions: {
                    column: {
                        stacking: 'normal',
                        borderRadius: 0,
                        pointPadding: 0.04,
                    },
                },
                series: this.breakdown.series.map((s) => ({
                    type: 'column',
                    name: s.name,
                    color: s.color,
                    data: s.data,
                })),
            })
        },
    },
    methods: {
        async drawGraphic(filter) {
            this.cache = {}
            this.filter = cloneFilterWith(filter)

            await this._loadMode()
        },
        async changeMode(mode) {
            if (this.mode === mode) {
                return
            }

            this.mode = mode
            await this._loadMode()
        },
        async _loadMode() {
            const mode = this.mode

            if (this.cache[mode]) {
                this.groups = this.cache[mode]
                return
            }

            this.loading = true
            this.error = false

            try {
                // Uma chamada só: o backend agrupa por canal/loja e já aplica os filtros selecionados.
                const groups = mode === 'money'
                    ? await dashboardService.getAmountOfMoneyEarnedGrouped(this.filter, this.dimension)
                    : await dashboardService.getAmountOfSalesGrouped(this.filter, this.dimension)

                this.cache = {...this.cache, [mode]: groups}

                // Ignora respostas de um modo que já não é o atual.
                if (this.mode === mode) {
                    this.groups = groups
                }
            } catch (err) {
                this.error = true
                this.groups = []
            } finally {
                this.loading = false
            }
        },
        _entryFor(group) {
            if (this.isChannel) {
                const channel = this.channelById(group.id)

                return {
                    id: group.id,
                    name: channel ? channel.name : `Canal #${group.id}`,
                    color: channel ? getChannelBrand(channel.enumChannel).color : undefined,
                    list: group.list,
                }
            }

            const store = (this.storeListState || []).find((st) => String(st.id) === String(group.id))

            return {
                id: group.id,
                name: store ? store.name : `Loja #${group.id}`,
                list: group.list,
            }
        },
        channelById(id) {
            if (!this.isChannel) {
                return null
            }

            return this.channelList.find((c) => String(c.id) === String(id))
                || this.filter.channelListWrapper.find((c) => String(c.id) === String(id))
                || null
        },
        formatValue(value) {
            if (this.isMoney) {
                return Number(value || 0).toLocaleString('pt-BR', {style: 'currency', currency: 'BRL'})
            }

            return Number(value || 0).toLocaleString('pt-BR')
        },
        formatShare(share) {
            return `${Number(share || 0).toLocaleString('pt-BR', {maximumFractionDigits: 1})}%`
        },
    },
    async mounted() {
        await this.drawGraphic(this.filter)
    },
}
</script>

<style lang="scss" scoped>
.sales-breakdown-mode {
    margin-top: -2px;

    .btn {
        min-width: 64px;
    }
}

.sales-breakdown-body {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 280px;
    gap: 20px;
    align-items: start;

    @media (max-width: 991px) {
        grid-template-columns: 1fr;
    }
}

.sales-breakdown-ranking {
    ul {
        margin: 0;
        padding: 0;
        list-style: none;
    }

    li {
        padding: 8px 0;
        border-top: 1px solid var(--border-subtle);
    }
}

.sales-breakdown-ranking-total {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: 4px;

    span {
        font-size: 11px;
        font-weight: 600;
        letter-spacing: .06em;
        text-transform: uppercase;
        color: var(--text-faint);
    }

    strong {
        font-size: 18px;
        color: var(--text-strong);
        font-variant-numeric: tabular-nums;
    }
}

.sales-breakdown-ranking-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    font-size: 13px;
}

.sales-breakdown-ranking-name {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    color: var(--text);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.sales-breakdown-dot {
    flex: none;
    width: 10px;
    height: 10px;
    border-radius: 50%;
}

.sales-breakdown-ranking-value {
    flex: none;
    font-weight: 600;
    color: var(--text-strong);
    font-variant-numeric: tabular-nums;

    small {
        margin-left: 4px;
        font-weight: 500;
        color: var(--text-faint);
    }
}

.sales-breakdown-bar {
    margin-top: 6px;
    height: 4px;
    border-radius: 999px;
    background-color: var(--surface-3);
    overflow: hidden;

    span {
        display: block;
        height: 100%;
        border-radius: 999px;
        transition: width .4s ease;
    }
}

.sales-breakdown-empty {
    padding: 24px 0;
    text-align: center;
    color: var(--text-faint);

    &.is-error {
        color: var(--danger-fg);
    }
}
</style>
